import getCountryCode from '../i18n/getCountryCode';
import {fullNameMinLength} from './fullName';
import {zipCodeFormat, zipCodeMaxLength} from './zipCode';
import {phoneNumberFormat, phoneNumberMaxLength} from './phoneNumber';
import isProject from '../selectors/isProject';

const REQUIRED = '^Required';
const INVALID = '^Invalid';
const SPANISH_INVALID = '^Invalido';
const SPANISH_REQUIRED = '^Obligatorio';

export function invalidMessage(){
  return isProject('vt_international_spanish_hagrid') ? SPANISH_INVALID : INVALID;
}

export function requiredMessage(){
  return isProject('vt_international_spanish_hagrid') ? SPANISH_REQUIRED : REQUIRED;
}

export function presenceRuleBuilder() {
  const presence = { message: requiredMessage(), allowEmpty: false };
  return { presence };
}

const NONEMPTY = { ...presenceRuleBuilder(), length: { minimum: 1, message: INVALID } };

// This function returns a callable function that builds a set of validation rules
function getValidationBuilder(fieldName){
  const VALIDATIONLOOKUP = {
    full_name: fullNameRuleBuilder,
    phone_number: phoneNumberRuleBuilder,
    zip_code: zipCodeRuleBuilder,
    dialer_consent_accepted: dialerConsentAcceptedRuleBuilder,
    received_tutoring_before: presenceRuleBuilder,
    student_name: nonEmptyRuleBuilder,
    pronoun: nonEmptyRuleBuilder,
    has_taken: nonEmptyRuleBuilder,
    test_score: scoreRuleBuilder,
    test_date: nonEmptyRuleBuilder,
    desired_score: scoreRuleBuilder,
    school_name: nonEmptyRuleBuilder,
    feeling: nonEmptyRuleBuilder,
    help_type: nonEmptyRuleBuilder,
    help_type_1: nonEmptyRuleBuilder,
    help_type_2: nonEmptyRuleBuilder,
    contact_person_type: nonEmptyRuleBuilder,
    contact_person_name: nonEmptyRuleBuilder,
    phone_type: nonEmptyRuleBuilder,
    day_to_call: nonEmptyRuleBuilder,
    time_to_call: nonEmptyRuleBuilder,
    knows_score: nonEmptyRuleBuilder,
    email: emailRuleBuilder,
    email_required: emailRuleBuilder,
    password: passwordRuleBuilder
  };
  let func = VALIDATIONLOOKUP[fieldName];
  if (func == null){
    func = defaultRuleBuilder;
  }
  return func;
}

// Default Rule Builder has no validation, so it is always valid
export function defaultRuleBuilder(){
  return { presence: { allowEmpty: true }};
}

// Rule Builder for full_name field
export function fullNameRuleBuilder(countryCode){
  return {
    ...presenceRuleBuilder(),
    length: {
      minimum: fullNameMinLength(countryCode),
      message: requiredMessage()
    }
  };
}

// Rule Builder for phone_number field
export function phoneNumberRuleBuilder(countryCode){
  return {
    ...presenceRuleBuilder(),
    length: {
      maximum: phoneNumberMaxLength(countryCode),
      message: invalidMessage()
    },
    format: {
      pattern: phoneNumberFormat(countryCode),
      message: invalidMessage()
    }
  };
}

// Rule Builder for zip_code field
export function zipCodeRuleBuilder(countryCode){
  return {
    ...presenceRuleBuilder(),
    length: {
      maximum: zipCodeMaxLength(countryCode),
      message: invalidMessage()
    },
    format: {
      pattern: zipCodeFormat(countryCode),
      message: invalidMessage()
    }
  };
}

export function scoreRuleBuilder(){
  return {
    ...presenceRuleBuilder(),
    numericality: {
      onlyInteger: true,
      message: INVALID
    }
  };
}

export function nonEmptyRuleBuilder(){
  return NONEMPTY;
}

// Rule Builder for email field
export function emailRuleBuilder(){
  return {
    ...presenceRuleBuilder(),
    email: {
      message: invalidMessage()
    }
  };
}

export function dialerConsentAcceptedRuleBuilder(){
  if (isProject(
    'vt_languages_tutoring_tou_hagrid',
    'ppc_locale_only_tou_hagrid'
  )) {
    return {
      length: { minimum: 0 }
    };
  } else {
    return presenceRuleBuilder();
  }
}

export function passwordRuleBuilder(){
  return {
  ...presenceRuleBuilder(),
    length: {
      minimum: 8,
      message: 'Too Short'
    }
  };
}

// Gets the validation rule for a given field name
export function createValidationRule(fieldName, countryCode){
  if(!countryCode) countryCode = getCountryCode();
  const validationBuilder = getValidationBuilder(fieldName);
  return validationBuilder(countryCode);
}

// Default export is the set of rules that are validated for the form
export default function(countryCode) {
  if(!countryCode) countryCode = getCountryCode();

  return {
    full_name: fullNameRuleBuilder(countryCode),
    phone_number: phoneNumberRuleBuilder(countryCode),
    zip_code: zipCodeRuleBuilder(countryCode),
    dialer_consent_accepted: dialerConsentAcceptedRuleBuilder(),
    received_tutoring_before: presenceRuleBuilder(),
    email_required: emailRuleBuilder(countryCode),
    password: passwordRuleBuilder(),
    company_name: nonEmptyRuleBuilder()
  };
}

export function allValidationRules(countryCode){
  if(!countryCode) countryCode = getCountryCode();

  return {
    full_name: fullNameRuleBuilder(countryCode),
    phone_number: phoneNumberRuleBuilder(countryCode),
    zip_code: zipCodeRuleBuilder(countryCode),
    dialer_consent_accepted: dialerConsentAcceptedRuleBuilder(),
    received_tutoring_before: presenceRuleBuilder(),
    student_name: defaultRuleBuilder(),
    pronoun: defaultRuleBuilder(),
    has_taken: defaultRuleBuilder(),
    test_score: scoreRuleBuilder(),
    test_date: defaultRuleBuilder(),
    desired_score: scoreRuleBuilder(),
    school_name: defaultRuleBuilder(),
    feeling: defaultRuleBuilder(),
    help_type: defaultRuleBuilder(),
    help_type_1: defaultRuleBuilder(),
    help_type_2: defaultRuleBuilder(),
    contact_person_type: defaultRuleBuilder(),
    contact_person_name: defaultRuleBuilder(),
    phone_type: defaultRuleBuilder(),
    day_to_call: defaultRuleBuilder(),
    time_to_call: defaultRuleBuilder(),
    knows_score: defaultRuleBuilder(),
    email: emailRuleBuilder(),
    email_required: emailRuleBuilder(),
    password: passwordRuleBuilder()
  };
}



// WEBPACK FOOTER //
// ./client/app/scripts/principles/validation/validationRules.js